import message from "@/utils/globalMessage";
import React, { useEffect, useState, useCallback } from "react";
import { Card, Table, Button, Typography, Spin, Form, Input, InputNumber, Switch, Space, Tag } from "antd";
import ResponsiveModal from "@components/ResponsiveModal";
import { useSelector } from "react-redux";
import { api } from "@redux/api";

import DeleteConfirm from "@components/Feedback/DeleteConfirm";
const { Text, Paragraph } = Typography;

/** Login API sends role as a single-key object, e.g. { "1": "Super User" } or { "1.0": "Super User" } (FloatField). */
function roleObjectAllowsCostAccountManage(role) {
  if (role == null) return false;
  if (typeof role === "number") return role === 1 || role === 3;
  if (typeof role === "object" && !Array.isArray(role)) {
    return Object.keys(role).some((k) => {
      const n = Number(k);
      return n === 1 || n === 3;
    });
  }
  return false;
}

/**
 * Company Admin: manage custom workorder cost account codes (maintenance/capital are built-in).
 */
const CompanyCostAccountSettings = ({ companyId }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const isCompanyAdmin = useSelector((s) => s.Auth?.is_companyadmin);
  const isSuperuser = useSelector((s) => s.Auth?.is_superuser);
  const role = useSelector((s) => s.Auth?.role);
  const canManage =
    Boolean(isSuperuser) ||
    Boolean(isCompanyAdmin) ||
    roleObjectAllowsCostAccountManage(role);

  const fetchList = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await api.getCostAccounts(companyId);
      if (res.status === 200 && Array.isArray(res.data)) {
        setRows(res.data);
      } else {
        setRows([]);
      }
    } catch (e) {
      message.error("Failed to load cost accounts.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      sort_order: 0,
      is_active: true,
    });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    if (record.is_builtin || !record.id) return;
    setEditing(record);
    form.setFieldsValue({
      code: record.code,
      label: record.label,
      sort_order: record.sort_order,
      is_active: record.is_active,
    });
    setModalOpen(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const cid = parseInt(companyId, 10);
      if (editing) {
        const res = await api.patchCostAccount(editing.id, {
          label: values.label,
          sort_order: values.sort_order,
          is_active: values.is_active,
        });
        if (res.status === 200) {
          message.success("Cost account updated.");
          setModalOpen(false);
          fetchList();
        }
      } else {
        const res = await api.createCostAccount({
          company: cid,
          code: values.code,
          label: values.label,
          sort_order: values.sort_order ?? 0,
          is_active: values.is_active !== false,
        });
        if (res.status === 201 || res.status === 200) {
          message.success("Cost account created.");
          setModalOpen(false);
          fetchList();
        }
      }
    } catch (e) {
      if (e?.errorFields) return;
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.code?.[0] ||
        e?.message ||
        "Save failed";
      message.error(typeof msg === "string" ? msg : "Save failed");
    }
  };

  const handleDelete = async (record) => {
    try {
      const res = await api.deleteCostAccount(record.id);
      if (res.status === 204 || res.status === 200) {
        message.success("Deleted.");
        fetchList();
      }
    } catch (e) {
      message.error("Delete failed.");
    }
  };

  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: 160,
    },
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
    },
    {
      title: "Type",
      key: "type",
      width: 120,
      render: (_, r) =>
        r.is_builtin ? <Tag color="blue">Built-in</Tag> : <Tag>Custom</Tag>,
    },
    {
      title: "Sort",
      dataIndex: "sort_order",
      key: "sort_order",
      width: 80,
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      width: 90,
      render: (v) => (v ? "Yes" : "No"),
    },
  ];

  if (canManage) {
    columns.push({
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_, record) => {
        if (record.is_builtin) return "—";
        return (
          <Space>
            <Button type="link" size="small" onClick={() => openEdit(record)}>
              Edit
            </Button>
            <DeleteConfirm
              title="Delete this cost account?"
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" size="small" danger>
                Delete
              </Button>
            </DeleteConfirm>
          </Space>
        );
      },
    });
  }

  return (
    <div style={{ padding: "20px 0" }}>
      <Card
        title={
          <span>
            <i className="fa-solid fa-sack-dollar pr-2" />
            Workorder Cost Accounts
          </span>
        }
        bordered={false}
      >
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          <Text strong>Maintenance</Text> and <Text strong>Capital</Text> are
          always available on workorders. Company admins can add custom account
          codes (e.g. energy, HVAC contract) for reporting and cost breakdown.
        </Paragraph>
        {canManage && (
          <div style={{ marginBottom: 12 }}>
            <Button type="primary" onClick={openCreate}>
              <i className="fa-solid fa-plus pr-2" />
              Add custom account
            </Button>
          </div>
        )}
        {!canManage && (
          <Paragraph type="secondary" style={{ marginBottom: 12 }}>
            Only company administrators can add or edit custom accounts.
          </Paragraph>
        )}
        <Spin spinning={loading}>
          <Table
            rowKey={(r) => (r.id != null ? `id-${r.id}` : `b-${r.code}`)}
            columns={columns}
            dataSource={rows}
            pagination={false}
            size="middle"
          />
        </Spin>
      </Card>

      <ResponsiveModal
        title={editing ? "Edit cost account" : "Add custom cost account"}
        open={modalOpen}
        onOk={handleModalOk}
        onCancel={() => setModalOpen(false)}
        destroyOnClose
        width={480}
      >
        <Form form={form} layout="vertical" autoComplete="off">
          {!editing && (
            <Form.Item
              name="code"
              label="Code"
              rules={[
                { required: true, message: "Required" },
                {
                  pattern: /^[a-z0-9_]{1,50}$/,
                  message:
                    "Lowercase letters, digits, underscores only (max 50)",
                },
              ]}
            >
              <Input placeholder="e.g. energy, hvac_contract" disabled={!!editing} />
            </Form.Item>
          )}
          <Form.Item
            name="label"
            label="Display label"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="Shown on workorder form" />
          </Form.Item>
          <Form.Item name="sort_order" label="Sort order">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </ResponsiveModal>
    </div>
  );
};

export default CompanyCostAccountSettings;
